import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';

interface Stat { count: number; last: number; min: number; max: number; avg: number; samples: number[]; last_dt: string; }

@Injectable({ providedIn: 'root' })
export class PerfService {
  private readonly LS_PAGE = 'page_perf';   // 每頁：click menu → 該頁最後一支 ajax
  private readonly LS_AJAX = 'ajax_perf';   // 每支 ajax：url → 耗時
  private readonly SETTLE_MS = 400;   // 最後一支 ajax 回來後，等這麼久沒新 ajax 才算頁面載完（串接型用）
  private readonly MAX_SAMPLES = 30;

  private currentPage = '';
  private startTime = 0;
  private active = 0;
  private pending = false;   // 這頁量測中
  private sawAjax = false;   // 這頁有發過 ajax 嗎
  private gen = 0;           // 換頁世代，隔離殘留請求
  private settleTimer: any = null;

  constructor(private router: Router) {
    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationStart) this.startPage(ev.url); // 點 menu 換頁 = 開始
    });
  }

  private startPage(url: string) {
    this.clearSettle();
    this.gen++;
    this.currentPage = (url || '/').split('?')[0];  // 去掉 query，同頁不同參數歸一頁
    this.startTime = performance.now();
    this.active = 0; this.pending = true; this.sawAjax = false;
  }

  // 攔截器呼叫：ajax 開始 → 回傳 token（含世代 / url / 起始時間）
  reqStart(url: string): { gen: number; url: string; start: number } {
    if (this.pending) { this.sawAjax = true; this.active++; this.clearSettle(); }
    return { gen: this.gen, url: url || '', start: performance.now() };
  }

  // 攔截器呼叫：ajax 結束(成功/失敗都算)
  reqEnd(tok: { gen: number; url: string; start: number }) {
    // (1) 每支 ajax 自己的耗時：不分頁、全部記
    this.saveTo(this.LS_AJAX, this.normalizeUrl(tok.url), Math.round(performance.now() - tok.start));

    // (2) 頁面量測：只算屬於當前這一頁的請求（世代要對得上）
    if (!this.pending || tok.gen !== this.gen) return;
    this.active = Math.max(0, this.active - 1);
    if (this.active === 0) {                   // 最後一支回來 → 等 settle 窗確認沒串接
      this.clearSettle();
      this.settleTimer = setTimeout(() => this.finish(), this.SETTLE_MS);
    }
  }

  private finish() {
    if (!this.pending || !this.sawAjax) { this.pending = false; return; }
    const ms = Math.round(performance.now() - this.startTime);
    this.pending = false;
    this.saveTo(this.LS_PAGE, this.currentPage, ms);
  }

  private clearSettle() { if (this.settleTimer) { clearTimeout(this.settleTimer); this.settleTimer = null; } }

  private normalizeUrl(url: string): string {
    const u = (url || '').split('?')[0];   // 去掉 query string，同端點歸一
    return u || '(unknown)';
  }

  private saveTo(lsKey: string, bucket: string, ms: number) {
    let all: { [k: string]: Stat } = {};
    try { all = JSON.parse(localStorage.getItem(lsKey) || '{}'); } catch { all = {}; }
    const s: Stat = all[bucket] || { count: 0, last: 0, min: ms, max: ms, avg: 0, samples: [], last_dt: '' };
    s.count++; s.last = ms; s.min = Math.min(s.min, ms); s.max = Math.max(s.max, ms);
    s.samples.push(ms); if (s.samples.length > this.MAX_SAMPLES) s.samples.shift();
    s.avg = Math.round(s.samples.reduce((a, b) => a + b, 0) / s.samples.length);
    s.last_dt = new Date().toISOString();
    all[bucket] = s;
    try { localStorage.setItem(lsKey, JSON.stringify(all)); } catch { }
  }

  getStats() { try { return JSON.parse(localStorage.getItem(this.LS_PAGE) || '{}'); } catch { return {}; } }       // 每頁
  getAjaxStats() { try { return JSON.parse(localStorage.getItem(this.LS_AJAX) || '{}'); } catch { return {}; } }   // 每支 ajax
  clearStats() { localStorage.removeItem(this.LS_PAGE); localStorage.removeItem(this.LS_AJAX); }
  get isBusy(): boolean { return this.pending && this.active > 0; }   // 額外：全域 loading 可用這個
}
